import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '@clerk/react'
import {
  fetchStoredDocuments,
  deleteFileWarning,
  describeStoredUse,
  deleteStoredDocument,
  fetchDocumentFile,
  formatFileSize,
  moveStoredDocument,
  whoSeesDrawer,
  DRAWER_LABELS,
  DocumentsError,
  type StoredDocument,
} from '@/lib/api'
import { saveBlob } from '@/lib/save-file'
import DrawerPicker from '@/components/DrawerPicker'
import { type DrawerChoice } from '@/lib/drawer-choice'

/**
 * THE OWNER'S STORED FILES (Kelvo build step 3c) — what is being held, and the
 * two things he can do about it: open one, or delete one.
 *
 * DELETE IS PERMANENT AND SAYS SO. The Worker removes the file from storage and
 * the record can never be restored, so the button is a two-step confirm — the
 * same shape lead erasure uses — and the second step names the consequence:
 * staff can no longer open it, and everything drawn from it stops being said
 * (A21), counted, before he confirms.
 *
 * `refreshKey` changes whenever something elsewhere on the screen changed what
 * is held (an upload, an approval), so the list is never stale beside the form
 * that just changed it. The other way round, `onChanged` is told after a file
 * is deleted or moved here — that drops or re-files its proposals — so the
 * waiting list above refreshes too (G52). A refresh of this list never calls
 * it: only a person's action does, so the two lists cannot loop.
 *
 * DRAWERS (Kelvo item 2). Each file shows its drawer and who that reaches, and
 * can be moved. A file in no drawer is flagged, and counted at the top: only
 * the owner sees what is in it, which is safe, but it is also invisible to the
 * staff it was probably meant for — so it is never left quiet.
 */
export default function StoredDocuments({
  refreshKey,
  onChanged,
}: {
  refreshKey: number
  /** Told after a delete or a move here changed what is waiting (G52). */
  onChanged?: () => void
}) {
  const onChangedRef = useRef(onChanged)
  onChangedRef.current = onChanged
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const [documents, setDocuments] = useState<StoredDocument[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoadError(null)
    try {
      setDocuments(await fetchStoredDocuments(await getTokenRef.current()))
    } catch {
      setLoadError('Could not load your documents.')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load, refreshKey])

  // After a person deleted or moved a file: this list reloads, and the page is
  // told, so the waiting list reloads with it.
  const changed = useCallback(() => {
    void load()
    onChangedRef.current?.()
  }, [load])

  return (
    <section className="mt-10">
      <h2 className="font-display text-xl font-semibold tracking-tight">Files kept for your staff</h2>
      <p className="mt-1 max-w-prose text-sm text-text-muted">
        When a document gives your assistant something to say, the file itself is kept here
        securely, so staff can open the original behind an answer. Only files that back an
        approved answer can be opened by staff.
      </p>
      <p className="mt-2 max-w-prose text-sm text-text-muted">
        A file is kept only while it is in use. Once you have withdrawn or reworded every answer
        that came from it, it is removed a week later, unless an answer offers it or it is on
        your renewals list.
      </p>
      {loadError ? (
        <p className="mt-3 text-sm text-text-danger">
          {loadError}{' '}
          <button onClick={() => void load()} className="underline underline-offset-2">
            Try again
          </button>
        </p>
      ) : documents === null ? (
        <p className="mt-3 text-text-muted">Loading…</p>
      ) : documents.length === 0 ? (
        <p className="mt-3 text-text-muted">No files kept yet. Upload one above.</p>
      ) : (
        <>
          <UnfiledNotice count={documents.filter((d) => d.drawer === null).length} />
          <ul className="mt-4 stack-y-3">
            {documents.map((d) => (
              <li key={d.id}>
                <StoredDocumentRow document={d} onChanged={changed} />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}

/** Said once, above the list, while any file is in no drawer. Nothing when none is. */
function UnfiledNotice({ count }: { count: number }) {
  if (count === 0) return null
  return (
    <p role="status" className="mt-3 rounded-lg border border-border-subtle bg-surface-base p-3 text-sm text-text-primary">
      {count === 1 ? '1 file is' : `${count} files are`} not in a drawer yet. Only you can see what is
      in {count === 1 ? 'it' : 'them'} until you choose a drawer below.
    </p>
  )
}

function StoredDocumentRow({ document: d, onChanged }: { document: StoredDocument; onChanged: () => void }) {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const [busy, setBusy] = useState<'open' | 'delete' | 'move' | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [moving, setMoving] = useState(false)
  const [target, setTarget] = useState<DrawerChoice>(d.drawer)
  const [error, setError] = useState<string | null>(null)

  // Build 12e: what uses the file, and when the sweep takes it if nothing does.
  const usage = describeStoredUse(d, Math.floor(Date.now() / 1000))
  const uploaded = new Date(d.uploadedAt * 1000).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })

  async function open() {
    setBusy('open')
    setError(null)
    try {
      saveBlob(await fetchDocumentFile(await getTokenRef.current(), d.id, 'owner'), d.name)
    } catch (e) {
      setError(e instanceof DocumentsError ? e.message : 'Could not open that document.')
    } finally {
      setBusy(null)
    }
  }

  async function remove() {
    setBusy('delete')
    setError(null)
    try {
      await deleteStoredDocument(await getTokenRef.current(), d.id)
      onChanged()
    } catch (e) {
      setError(e instanceof DocumentsError ? e.message : 'That did not delete. Try again.')
      setBusy(null)
      // A delete stops the file's answers before anything else (A21), so even
      // a failed one may have changed what is waiting: show what is true.
      onChanged()
    }
  }

  /**
   * Move it. '' (nothing chosen) cannot be sent; the button is disabled until
   * a real choice differs from where it is. On a 409 the file moved elsewhere
   * a moment ago: the engine's sentence is shown and the list reloads, so the
   * screen shows where it really is rather than where this row thought.
   */
  async function move() {
    if (target === '' || target === d.drawer) return
    setBusy('move')
    setError(null)
    try {
      await moveStoredDocument(await getTokenRef.current(), d.id, target)
      setMoving(false)
      setBusy(null)
      onChanged()
    } catch (e) {
      setError(e instanceof DocumentsError ? e.message : 'That did not move. Try again.')
      setBusy(null)
      if (e instanceof DocumentsError && e.status === 409) onChanged()
    }
  }

  return (
    <div className="rounded-card border border-border-subtle bg-surface-raised p-4">
      <p className="break-words text-sm font-medium text-text-primary">{d.name}</p>
      <p className="mt-0.5 text-xs text-text-muted">
        {formatFileSize(d.byteSize)} · added {uploaded} · {usage.use}
      </p>
      {usage.removal ? (
        <p className="mt-1 text-xs font-medium text-text-primary">{usage.removal}</p>
      ) : null}
      <p className={`mt-1 text-xs ${d.drawer === null ? 'font-medium text-text-danger' : 'text-text-muted'}`}>
        {d.drawer === null
          ? 'Not in a drawer — only you can see what is in it'
          : `${DRAWER_LABELS[d.drawer]} · ${whoSeesDrawer(d.drawer)}`}
      </p>

      {moving ? (
        <div className="mt-3">
          <DrawerPicker
            id={`move-${d.id}`}
            label="Move it to"
            hint="Everything your assistant says from this file moves with it."
            value={target}
            onChange={setTarget}
            disabled={busy !== null}
            allowUnfiled
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              onClick={() => void move()}
              disabled={busy !== null || target === '' || target === d.drawer}
              aria-busy={busy === 'move'}
              className="rounded-lg bg-brand-primary px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
            >
              {busy === 'move' ? 'Moving…' : 'Move'}
            </button>
            <button
              onClick={() => {
                setMoving(false)
                setTarget(d.drawer)
              }}
              disabled={busy !== null}
              className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-primary transition-colors hover:bg-surface-base disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {confirming ? (
        <div className="mt-3">
          <p className="text-sm text-text-primary">{deleteFileWarning(d)}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              onClick={() => void remove()}
              disabled={busy !== null}
              aria-busy={busy === 'delete'}
              className="rounded-lg bg-text-danger px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
            >
              {busy === 'delete' ? 'Deleting…' : 'Yes, delete it'}
            </button>
            <button
              onClick={() => setConfirming(false)}
              disabled={busy !== null}
              className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-primary transition-colors hover:bg-surface-base disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
            >
              Keep it
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            onClick={() => void open()}
            disabled={busy !== null}
            aria-busy={busy === 'open'}
            className="rounded-lg bg-brand-primary px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
          >
            {busy === 'open' ? 'Opening…' : 'Open'}
          </button>
          {!moving ? (
            <button
              onClick={() => {
                setTarget(d.drawer === null ? '' : d.drawer)
                setMoving(true)
              }}
              disabled={busy !== null}
              className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-primary transition-colors hover:bg-surface-base disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
            >
              {d.drawer === null ? 'Choose a drawer' : 'Move'}
            </button>
          ) : null}
          <button
            onClick={() => setConfirming(true)}
            disabled={busy !== null}
            className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-danger transition-colors hover:bg-surface-base disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
          >
            Delete
          </button>
        </div>
      )}

      {error ? (
        <p role="alert" className="mt-2 text-xs text-text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}
